import { inject, Injectable, NgZone } from "@angular/core";
import { Data } from "@angular/router";
import { getAuth } from "firebase/auth";
import { firstValueFrom, of, Subscription } from "rxjs";
import { catchError, filter, take, timeout } from "rxjs/operators";
import { CheckoutFlowService, resolvePaymentVisitorId } from "src/app/services/checkout-flow.service";
import { CommonService } from "src/app/services/common/common.service";
import { PaymentService } from "src/app/services/payment.service";
import { SalesJourneyIdentityService } from "src/app/services/sales-journey-identity.service";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";

export interface StoreCheckoutFlowHost {
  keys: Data;
  language: string;
  paymentMode: string;
  pathString: string;
  checkOutComData?: Data;
  purchaseDetail$?: import("rxjs").BehaviorSubject<Data | null>;
  purchaseDetail?: Data;
  userData?: Data;
  preferredCurrency: string;
  selectedDiscountValue: number;
  updatedPrizeValue: number;
  paymentReason?: string;
  isCheckoutComform: boolean;
  isButtonDisabled: boolean;
  showCheckOutComSpinner: boolean;
  errorMessage: string;
  checkoutFlowReady?: boolean;
  cardTokenizedCalled: boolean;
  addNewCardInProgress?: boolean;
  getCookie?: (name: string) => string | null;
  getCheckOutComResponseSubscription?: Subscription;
  onFlowPaymentCompleted?: () => void;
  onFlowReady?: () => void;
}

@Injectable({ providedIn: "root" })
export class StoreCheckoutFlowService {
  private checkoutFlowService = inject(CheckoutFlowService);
  private paymentService = inject(PaymentService);
  private commonService = inject(CommonService);
  private ngZone = inject(NgZone);
  private salesJourneyIdentityService = inject(SalesJourneyIdentityService);

  private static readonly PURCHASE_DETAIL_STORAGE_KEY = "addCardPurchaseDetail";

  private static calculatePaymentAmount(basePrice: number, addOnTotal: number, voucherDiscount: number): number {
    const base = Number(basePrice) || 0;
    const addons = Number(addOnTotal) || 0;
    const discount = Number(voucherDiscount) || 0;
    const total = base + addons;
    return total - total * discount;
  }

  /** Persist purchase detail so Checkout.com Flow 3DS redirects do not lose isTopUp/page. */
  persistPurchaseDetailForRedirect(pathString: string, purchaseDetail: Data | null | undefined): void {
    if (!pathString || !purchaseDetail) {
      return;
    }
    try {
      localStorage.setItem(
        StoreCheckoutFlowService.PURCHASE_DETAIL_STORAGE_KEY,
        JSON.stringify({ pathString, purchaseDetail }),
      );
    } catch {
      // Ignore storage failures; success path will fall back to in-memory state.
    }
  }

  restorePurchaseDetailForRedirect(pathString?: string): Data | null {
    try {
      const raw = localStorage.getItem(StoreCheckoutFlowService.PURCHASE_DETAIL_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (pathString && parsed?.pathString && String(parsed.pathString) !== String(pathString)) {
        return null;
      }
      return parsed?.purchaseDetail ?? null;
    } catch {
      return null;
    }
  }

  clearPersistedPurchaseDetail(): void {
    try {
      localStorage.removeItem(StoreCheckoutFlowService.PURCHASE_DETAIL_STORAGE_KEY);
    } catch {
      // no-op
    }
  }

  unmount(): void {
    this.checkoutFlowService.unmount();
  }

  submitPayment(): void {
    this.checkoutFlowService.submitPayment();
  }

  isCheckoutLoadingMessage(message: string): boolean {
    return (message || "").trim().toLowerCase() === "loading secure card form...";
  }

  async mountStoreCheckoutFlow(host: StoreCheckoutFlowHost): Promise<void> {
    const userId = await firstValueFrom(this.commonService.getUid("storeCheckoutFlow"));
    if (!userId) {
      throw new Error("Unable to resolve user for checkout");
    }

    if (!host.keys?.publicKey) {
      throw new Error("Checkout publicKey is not configured");
    }

    host.isCheckoutComform = true;
    host.checkoutFlowReady = false;
    host.errorMessage = "Loading secure card form...";
    host.showCheckOutComSpinner = true;
    host.isButtonDisabled = true;

    if (host.pathString) {
      this.paymentService.setcheckOutComDocId({ docId: String(host.pathString) });
    }

    const paymentData = this.buildPaymentData(host, userId);
    if (host.pathString) {
      this.persistPurchaseDetailForRedirect(host.pathString, host.purchaseDetail$?.value ?? host.purchaseDetail);
    }
    const componentOptions = this.checkoutFlowService.buildComponentOptionsFromPaymentData(
      host.keys,
      paymentData as Record<string, unknown>,
    );

    await this.checkoutFlowService.mountForCardEntry({
      container: "#cko-flow-container",
      publicKey: host.keys.publicKey,
      environment: this.checkoutFlowService.getFlowEnvironment(host.paymentMode, host.keys.publicKey),
      locale: this.checkoutFlowService.mapLocale(host.language || "EN-GB"),
      componentOptions,
      createDisplaySession: () => this.createDisplaySession(host, paymentData),
      onReady: () =>
        this.ngZone.run(() => {
          host.checkoutFlowReady = true;
          host.showCheckOutComSpinner = false;
          host.isButtonDisabled = false;
          if (this.isCheckoutLoadingMessage(host.errorMessage)) {
            host.errorMessage = "";
          }
          host.onFlowReady?.();
        }),
      onPaymentCompleted: (_component, paymentResponse) =>
        this.ngZone.run(() => {
          host.cardTokenizedCalled = true;
          host.addNewCardInProgress = true;
          host.showCheckOutComSpinner = true;
          host.isCheckoutComform = false;
          host.isButtonDisabled = true;
          host.onFlowPaymentCompleted?.();

          const logId = host.pathString ? String(host.pathString) : "";
          const paymentId = paymentResponse?.id ?? paymentResponse?.payment_id ?? paymentResponse?.paymentId;
          if (!logId || !paymentId) {
            return;
          }

          this.redirectToCheckoutResponseHandler({
            status: "success",
            logId,
            userId,
            paymentId: String(paymentId),
            sandbox: host.paymentMode === "sandbox",
          });
        }),
      onError: (_component, error) =>
        this.ngZone.run(() => {
          const paymentId = error?.details?.paymentId;
          const logId = host.pathString ? String(host.pathString) : "";

          if (paymentId && logId && userId) {
            this.redirectToCheckoutResponseHandler({
              status: "fail",
              logId,
              userId,
              paymentId: String(paymentId),
              sandbox: host.paymentMode === "sandbox",
            });
            return;
          }

          host.errorMessage = error?.message || "Failed to load payment form";
          host.showCheckOutComSpinner = false;
          host.isButtonDisabled = true;
          host.onFlowReady?.();
        }),
    });
  }

  private redirectToCheckoutResponseHandler(options: {
    status: "success" | "fail";
    logId: string;
    userId: string;
    paymentId: string;
    sandbox: boolean;
  }): void {
    const params = new URLSearchParams({
      status: options.status,
      logId: options.logId,
      userId: options.userId,
      "cko-payment-id": options.paymentId,
    });
    if (options.sandbox) {
      params.set("sandbox", "true");
    }

    window.location.href = `${environment.onSuccessRedirectUrl}/${environment.cloudFunctions.checkoutFlowResponseHandler}?${params}`;
  }

  waitForPaymentConfirmation(
    host: StoreCheckoutFlowHost,
    handlers: {
      onSuccess: (paymentResData: Data) => void;
      onFailure: () => void;
      onTimeout?: () => void;
    },
  ): Subscription {
    host.getCheckOutComResponseSubscription?.unsubscribe();

    const subscription = this.paymentService.getCheckOutComResponse$
      .pipe(
        filter((paymentResData: Data) => {
          const status = paymentResData?.payment_response?.payment_status;
          return status === "success" || status === "fail";
        }),
        take(1),
        timeout(120000),
        catchError(() => of(null)),
      )
      .subscribe((paymentResData: Data | null) => {
        this.ngZone.run(() => {
          host.showCheckOutComSpinner = false;
          host.addNewCardInProgress = false;

          if (!paymentResData?.payment_response) {
            handlers.onTimeout?.();
            handlers.onFailure();
            return;
          }

          if (paymentResData.payment_response.payment_status === "success") {
            handlers.onSuccess(paymentResData);
            return;
          }

          handlers.onFailure();
        });
      });

    host.getCheckOutComResponseSubscription = subscription;
    return subscription;
  }

  private async createDisplaySession(host: StoreCheckoutFlowHost, paymentData: Data): Promise<Data> {
    const response = await firstValueFrom(
      this.checkoutFlowService.createFlowSession(host.paymentMode, paymentData as Record<string, unknown>),
    );
    const session = this.checkoutFlowService.extractPaymentSession(response as Data);
    if (!session) {
      throw new Error("Failed to create checkout flow display session");
    }
    return session;
  }

  private buildPaymentData(host: StoreCheckoutFlowHost, userId: string): Data {
    const purchaseDetail = host.purchaseDetail$?.value ?? host.purchaseDetail ?? {};
    host.paymentReason = purchaseDetail?.page === "store" ? "offer" : "award";

    const amt = host.checkOutComData?.prize || purchaseDetail?.prize;
    const msn = host.checkOutComData?.msn || host.userData?.msn;
    const userNumber = msn?.substring(2);
    const auth = getAuth();
    const TP1 = localStorage.getItem("TP1") || "";
    const TP2 = localStorage.getItem("TP2") || "";
    const TP3 = localStorage.getItem("TP3") || "";
    const T_ID = localStorage.getItem("T_ID") || "";
    const voucherDiscount = host.selectedDiscountValue || 0;
    const valuePrize = purchaseDetail?.offers?.price || purchaseDetail?.prize;
    const baseAmount = parseFloat(amt);
    const discountedTotal = StoreCheckoutFlowService.calculatePaymentAmount(
      baseAmount,
      host.updatedPrizeValue,
      voucherDiscount,
    );

    const paymentData: Data = {
      amount: purchaseDetail?.add_on?.length > 0 ? discountedTotal : parseFloat(amt) * (1 - voucherDiscount),
      currency: host.preferredCurrency,
      email: host.checkOutComData?.email || msn + "@lottosocial.com",
      name: host.checkOutComData?.name || host.userData?.name,
      phone: msn,
      userId,
      logId: host.pathString,
      msn: {
        country_code: `+${host.checkOutComData?.country_code || host.userData?.country_code}`,
        number: userNumber,
      },
      payment_reason: host.paymentReason,
      offer_id: purchaseDetail?.offers?.offerid ? purchaseDetail.offers.offerid : purchaseDetail?.offer_id,
      prosub_id: purchaseDetail?.prosub_id ? purchaseDetail.prosub_id : localStorage.getItem("prosub_id"),
      anonymous_id: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
      tp1: TP1 === "null" ? "" : TP1,
      tp2: TP2 === "null" ? "" : TP2,
      tp3: TP3 === "null" ? "" : TP3,
      t_id: T_ID === "null" ? "" : T_ID,
      Other: purchaseDetail.other
        ? purchaseDetail.other
        : localStorage.getItem("Other")
          ? localStorage.getItem("Other")
          : host.getCookie?.("Other")
            ? host.getCookie("Other")
            : "",
      value: valuePrize - valuePrize * voucherDiscount,
      preferred_currency: {
        Value: purchaseDetail.offers?.int_price?.[host.preferredCurrency]
          ? purchaseDetail.offers.int_price[host.preferredCurrency] -
            purchaseDetail.offers.int_price[host.preferredCurrency] * voucherDiscount
          : purchaseDetail?.prize - purchaseDetail?.prize * voucherDiscount,
        currency: host.preferredCurrency,
      },
      p_type: purchaseDetail?.p_type ? purchaseDetail.p_type : "21",
      visitorid: resolvePaymentVisitorId(),
      requestid: uuidv4(),
      product_description: purchaseDetail?.isTopUp ? "Award" : (purchaseDetail?.product_description ?? ""),
      add_on: purchaseDetail?.add_on ?? [],
      return_url: window.location.href,
    };

    if (paymentData.amount == null || isNaN(paymentData.amount)) {
      paymentData.amount = paymentData.value ?? 0;
    }

    const paymentDataWithIdentity = this.salesJourneyIdentityService.applyIdentityEnvelope(paymentData, {
      userId: auth?.currentUser?.isAnonymous ? null : (userId ?? null),
      anonymousId: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
      // Omit customerId: resolve the actual SQL ID when available.
      msn: msn,
      checkoutAttemptId: host.pathString ? String(host.pathString) : null,
    });

    return paymentDataWithIdentity;
  }
}
